// Shared building blocks of the tender views — used by the feed cards AND the
// dedicated proceeding page (/ezamowienia/p/:id).

import { useState } from "react";
import { Link } from "react-router-dom";
import { Bot, Calculator, ChevronDown, ChevronRight, ExternalLink, Sparkles } from "lucide-react";
import { clsx } from "clsx";

import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";

import {
  useEzBrowserFetch,
  useEzBrowserRuns,
  useEzDocuments,
  useEzEstimate,
  useEzProceeding,
  useEzRecommend,
  useEzSimilar,
} from "./hooks";
import type { ProceedingRow } from "./types";
import {
  DeadlinePill,
  DecisionControl,
  STAGE_META,
  StageChip,
  TagChip,
  WorkModeChip,
  fmtDate,
  fmtPln,
} from "./ui";

export function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">{label}</p>
      <p className="text-sm mt-0.5">{children}</p>
    </div>
  );
}

const RECO_META: Record<string, { label: string; cls: string }> = {
  start: { label: "EZ-ARON: Startuj", cls: "bg-emerald-600 text-white" },
  watch: { label: "EZ-ARON: Obserwuj", cls: "bg-sky-600 text-white" },
  pass: { label: "EZ-ARON: Odpuść", cls: "bg-gray-500 text-white" },
};

export function RecoChip({ row }: { row: ProceedingRow }) {
  const m = RECO_META[row.recommendation];
  if (!m) return null;
  return (
    <span
      className={clsx("text-[10px] font-bold rounded-full px-2 py-0.5 whitespace-nowrap", m.cls)}
      title={row.recommendation_reason || undefined}
    >
      {m.label}
    </span>
  );
}

/* Ask EZ-ARON for a verdict. Reads the notice, the comparables and — if the
   browser agent already fetched them — the tender documents themselves. */
function RecommendButton({ id, again = false }: { id: string; again?: boolean }) {
  const reco = useEzRecommend();
  const detail = useEzProceeding(id);
  const waiting = reco.isPending || (reco.isSuccess && !detail.data?.recommendation);
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Button variant={again ? "ghost" : "secondary"} disabled={waiting} onClick={() => reco.mutate(id)}>
        {waiting ? (
          <>
            <Spinner /> <span className="ml-1.5">EZ-ARON analizuje…</span>
          </>
        ) : again ? (
          "Przeanalizuj ponownie"
        ) : (
          <>
            <Sparkles size={13} className="mr-1" />
            Analiza AI — startować?
          </>
        )}
      </Button>
      {!again && (
        <span className="text-[11px] text-[var(--color-text-muted)]">
          czyta ogłoszenie, podobne rozstrzygnięte i pobraną dokumentację (SWZ, opis
          przedmiotu, przedmiar, wzór umowy) → startuj / obserwuj / odpuść z widełkami
          i ryzykami
        </span>
      )}
      {reco.isError && (
        <span className="text-xs text-red-600">Nie udało się zlecić analizy.</span>
      )}
    </div>
  );
}

/* Why EZ-ARON chose start/watch/pass — reasoning, price range, risks. */
export function RecommendationBlock({ row, framed = false }: { row: ProceedingRow; framed?: boolean }) {
  if (!row.recommendation)
    return (
      <div className={framed ? "" : "mt-3 pt-3 border-t border-[var(--color-border)]"}>
        <RecommendButton id={row.id} />
      </div>
    );
  const d = row.recommendation_data;
  return (
    <div
      className={
        framed
          ? "rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-3"
          : "mt-3 pt-3 border-t border-[var(--color-border)]"
      }
    >
      <div className="flex items-center gap-2 flex-wrap mb-1.5">
        <RecoChip row={row} />
        {d?.widelki_od != null && d?.widelki_do != null ? (
          <span className="text-sm">
            {d.widelki_pewnosc === "niska" ? "orientacyjne widełki (niska pewność):" : "sensowna oferta:"}{" "}
            <b className="text-emerald-600">
              {fmtPln(String(d.widelki_od))} – {fmtPln(String(d.widelki_do))}
            </b>
          </span>
        ) : (
          <span className="text-[11px] text-amber-700 dark:text-amber-400">
            widełki: brak podstaw — podobne dobrane po nazwie, skala zakresu
            {d?.skala ? ` (${d.skala})` : ""} nieporównywalna; realną wycenę da przedmiar
            („Wyceń min/śr/max”)
          </span>
        )}
        {d?.podobnych != null && (
          <span className="text-[11px] text-[var(--color-text-muted)]">
            kontekst: {d.podobnych} podobnych z nazwy (mediana{" "}
            {fmtPln(d.mediana_podobnych ?? null)})
          </span>
        )}
      </div>
      <p className="text-sm whitespace-pre-wrap">{row.recommendation_reason}</p>
      <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">
        {(d?.dokumenty_przeczytane?.length ?? 0) > 0 ? (
          <>przeczytane pliki: {d!.dokumenty_przeczytane!.join(", ")}</>
        ) : (
          <>oparte tylko na treści ogłoszenia — pobierz dokumentację, żeby AI przeczytało SWZ</>
        )}
      </p>
      {(d?.kluczowe_ryzyka?.length ?? 0) > 0 && (
        <ul className="mt-1.5 space-y-1">
          {d!.kluczowe_ryzyka!.map((r, i) => {
            const risk = typeof r === "string" ? { ryzyko: r } : r;
            return (
              <li key={i} className="text-xs">
                <span className="text-amber-700 dark:text-amber-400">⚠ {risk.ryzyko}</span>
                {risk.cytat && (
                  <span className="block pl-4 text-[11px] italic text-[var(--color-text-muted)]">
                    {risk.zrodlo || "źródło"}: „{risk.cytat}”
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <div className="mt-2">
        <RecommendButton id={row.id} again />
      </div>
    </div>
  );
}

const EXT_CLS: [string[], string][] = [
  [["pdf"], "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"],
  [["doc", "docx", "rtf", "odt"], "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"],
  [["xls", "xlsx", "ods", "csv"], "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"],
  [["zip", "7z", "rar"], "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"],
];

function ExtChip({ fileName }: { fileName: string }) {
  const ext = (fileName.split(".").pop() || "").toLowerCase();
  const cls =
    EXT_CLS.find(([exts]) => exts.includes(ext))?.[1] ??
    "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300";
  return (
    <span className={clsx("w-11 shrink-0 text-center text-[10px] font-bold uppercase rounded px-1 py-0.5", cls)}>
      {ext || "?"}
    </span>
  );
}

const ESTIMATABLE_EXTS = ["pdf", "zip", "7z"];

const RUN_META: Record<string, { cls: string }> = {
  running: { cls: "text-sky-700 dark:text-sky-400" },
  done: { cls: "text-emerald-700 dark:text-emerald-400" },
  empty: { cls: "text-amber-700 dark:text-amber-400" },
  error: { cls: "text-red-600" },
};

/* The browser agent: on click it walks the buyer's procurement platform, finds
   this proceeding among the others and downloads its attachments. Never runs by
   itself — section 3.1 of a notice often points at a general platform page, and
   getting from there to the files is navigation no parser can do. */
function BrowserAgentBlock({ id, offerButton = false }: { id: string; offerButton?: boolean }) {
  const runs = useEzBrowserRuns(id, true);
  const fetchDocs = useEzBrowserFetch();
  const [openTrace, setOpenTrace] = useState(false);

  const latest = runs.data?.[0];
  const running = latest?.status === "running" || fetchDocs.isPending;
  const conflict = (fetchDocs.error as { response?: { status?: number } } | null)?.response
    ?.status;
  const refusal =
    (
      fetchDocs.error as
        | { response?: { data?: { errors?: { detail?: string } } } }
        | null
    )?.response?.data?.errors?.detail ?? "";

  if (!offerButton && !latest) return null;

  return (
    <div className="mt-2">
      {offerButton && (
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="secondary" disabled={running} onClick={() => fetchDocs.mutate(id)}>
            <Bot size={13} className="mr-1" />
            {running ? "Agent pracuje na platformie…" : "Wejdź na platformę i pobierz dokumenty"}
          </Button>
          <span className="text-[11px] text-[var(--color-text-muted)]">
            agent klika na platformie zamiast Ciebie — kilka groszy za postępowanie, tylko na
            Twoje kliknięcie
          </span>
        </div>
      )}
      {refusal && (
        <p className={clsx("text-xs mt-1.5", conflict === 409 ? "text-amber-700" : "text-red-600")}>
          {refusal}
        </p>
      )}
      {latest && (
        <div className="mt-1.5 text-xs">
          <div className="flex items-center gap-2 flex-wrap">
            {running && <Spinner />}
            <span className={clsx("font-medium", RUN_META[latest.status]?.cls)}>
              {latest.status === "running"
                ? "Agent chodzi po platformie…"
                : latest.status === "done"
                  ? `Agent pobrał ${latest.documents_found} plik(ów)`
                  : latest.status === "empty"
                    ? "Agent nie znalazł plików do pobrania"
                    : "Agent nie dał rady"}
            </span>
            {latest.cost_usd && (
              <span className="text-[10px] text-[var(--color-text-muted)] tabular-nums">
                koszt {latest.cost_usd} USD
              </span>
            )}
            {latest.steps.length > 0 && (
              <button
                onClick={() => setOpenTrace((v) => !v)}
                className="text-[11px] underline text-[var(--color-text-muted)] hover:text-rose-700"
              >
                {openTrace ? "ukryj kroki" : "co zrobił agent"}
              </button>
            )}
          </div>
          {(latest.summary || latest.error) && (
            <p className="mt-1 text-[var(--color-text-muted)]">{latest.error || latest.summary}</p>
          )}
          {openTrace && (
            <ol className="mt-1.5 space-y-0.5 border-l-2 border-[var(--color-border)] pl-2.5">
              {latest.steps.map((s, i) => (
                <li
                  key={i}
                  className={clsx(
                    "text-[11px]",
                    s.type === "result"
                      ? "text-[var(--color-text-muted)] italic"
                      : "text-[var(--color-text)]",
                  )}
                >
                  {s.text}
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}

/* SWZ documents from whichever platform hosts them, with the przedmiar flag and
   the "Wyceń min/śr/max" bridge into the estimating pipeline. */
export function DocumentsSection({ id, autoOpen = false }: { id: string; autoOpen?: boolean }) {
  const [armed, setArmed] = useState(autoOpen);
  const docs = useEzDocuments(id, armed);
  const estimate = useEzEstimate();

  if (!armed)
    return (
      <div className="mt-3">
        <Button variant="secondary" onClick={() => setArmed(true)}>
          Dokumenty postępowania (SWZ) →
        </Button>
      </div>
    );
  if (docs.isLoading)
    return (
      <div className="mt-3 flex justify-center py-2">
        <Spinner />
      </div>
    );
  if (docs.isError)
    return (
      <p className="text-xs text-red-600 mt-3">
        Platforma nie zwróciła dokumentów (spróbuj ponownie za chwilę).
      </p>
    );

  const rows = docs.data?.documents ?? [];
  const platformUrl = docs.data?.platform_url ?? "";
  const platformLine = platformUrl ? (
    <p className="text-[11px] text-[var(--color-text-muted)]">
      Strona postępowania:{" "}
      <a
        href={platformUrl}
        target="_blank"
        rel="noreferrer"
        className="text-rose-700 dark:text-rose-400 hover:underline break-all"
      >
        {platformUrl.replace(/^https?:\/\//, "").slice(0, 70)}
      </a>
    </p>
  ) : null;

  if (rows.length === 0)
    return (
      <div className="mt-3">
        <p className="text-xs text-[var(--color-text-muted)]">
          {platformUrl
            ? "Tej platformy nie czytamy wprost — mogę tam wejść i poszukać plików:"
            : "Brak opublikowanych dokumentów."}
        </p>
        {platformLine}
        {platformUrl && <BrowserAgentBlock id={id} offerButton />}
      </div>
    );

  return (
    <div className="mt-3">
      {platformLine}
      <BrowserAgentBlock id={id} />
      {estimate.isSuccess && (
        <p className="text-xs text-emerald-600 mt-1.5">
          Wycena zlecona ({estimate.data.files} plik/i) —{" "}
          <Link to={`/estimating/${estimate.data.job_id}`} className="underline">
            otwórz w module kosztorysowym
          </Link>{" "}
          (AI wyciągnie pozycje, rozwinie normy KNR i policzy warianty min/śr/max).
        </p>
      )}
      {estimate.isError && (
        <p className="text-xs text-red-600 mt-1.5">Nie udało się zlecić wyceny tego pliku.</p>
      )}
      <div className="mt-1.5 rounded-lg border border-[var(--color-border)] divide-y divide-[var(--color-border)] overflow-hidden">
        {rows.map((d) => {
          const ext = (d.file_name.split(".").pop() || "").toLowerCase();
          const expired = !d.download_url;
          const estimable = ESTIMATABLE_EXTS.includes(ext) && !expired;
          return (
            <div
              key={d.document_id}
              className="flex items-center gap-2.5 px-2.5 py-1.5 text-xs hover:bg-black/[.03] dark:hover:bg-white/[.05] transition-colors group"
            >
              <ExtChip fileName={d.file_name} />
              {expired ? (
                <span
                  title={`${d.file_name} — plik wygasł w naszym magazynie, pobierz go ponownie z platformy`}
                  className="min-w-0 flex-1 truncate text-[var(--color-text-muted)] line-through"
                >
                  {d.name || d.file_name}
                </span>
              ) : (
                <a
                  href={d.download_url!}
                  target="_blank"
                  rel="noreferrer"
                  title={d.file_name}
                  className="min-w-0 flex-1 truncate hover:text-rose-700 dark:hover:text-rose-400"
                >
                  {d.name || d.file_name}
                </a>
              )}
              {expired && (
                <span
                  title="Dokumentacja przetargowa jest publiczna i zawsze do pobrania z platformy, więc nie trzymamy jej u siebie bez końca."
                  className="shrink-0 text-[10px] font-semibold rounded-full px-2 py-0.5 bg-gray-200 text-gray-600 dark:bg-gray-800 dark:text-gray-300"
                >
                  WYGASŁ
                </span>
              )}
              {d.is_przedmiar && (
                <span className="shrink-0 text-[10px] font-bold rounded-full px-2 py-0.5 bg-rose-600 text-white">
                  PRZEDMIAR
                </span>
              )}
              {estimable && (
                <button
                  disabled={estimate.isPending}
                  onClick={() =>
                    estimate.mutate({ id, documentId: d.document_id, fileName: d.file_name })
                  }
                  title="Prześlij do modułu kosztorysowego: AI wyciągnie pozycje, rozwinie normy KNR i policzy warianty min/śr/max wg SEKOCENBUD"
                  className={clsx(
                    "shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-2 py-0.5 border transition-colors",
                    d.is_przedmiar
                      ? "border-rose-600 text-rose-700 dark:text-rose-400 hover:bg-rose-600 hover:text-white"
                      : "border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-rose-400 hover:text-rose-700",
                  )}
                >
                  <Calculator size={11} />
                  {estimate.isPending ? "Zlecanie…" : "Wyceń min/śr/max"}
                </button>
              )}
              {d.published_at && (
                <span className="shrink-0 text-[10px] text-[var(--color-text-muted)] tabular-nums">
                  {fmtDate(d.published_at)}
                </span>
              )}
              {!expired && (
                <a href={d.download_url!} target="_blank" rel="noreferrer" aria-label="Pobierz">
                  <ExternalLink
                    size={12}
                    className="shrink-0 text-[var(--color-text-muted)] group-hover:text-rose-600"
                  />
                </a>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* Comparable resolved tenders with known prices & offer counts. */
export function SimilarSection({ id, autoOpen = false }: { id: string; autoOpen?: boolean }) {
  const [armed, setArmed] = useState(autoOpen);
  const similar = useEzSimilar(id, armed);

  if (!armed)
    return (
      <div className="mt-3 pt-3 border-t border-[var(--color-border)]">
        <Button variant="secondary" onClick={() => setArmed(true)}>
          Analiza podobnych wygranych →
        </Button>
        <span className="text-[11px] text-[var(--color-text-muted)] ml-2">
          znane ceny, liczba ofert i rozbieżność w porównywalnych postępowaniach
        </span>
      </div>
    );
  if (similar.isLoading)
    return (
      <div className="mt-3 pt-3 border-t border-[var(--color-border)] flex justify-center py-3">
        <Spinner />
      </div>
    );
  const d = similar.data;
  if (!d) return <p className="text-sm text-red-600 mt-3">Analiza nie powiodła się.</p>;
  if (d.aggregates.n === 0)
    return (
      <p className="text-sm text-[var(--color-text-muted)] mt-3 pt-3 border-t border-[var(--color-border)]">
        Brak porównywalnych rozstrzygniętych postępowań w bazie (jeszcze).
      </p>
    );
  const a = d.aggregates;
  return (
    <div className="mt-3 pt-3 border-t border-[var(--color-border)]">
      <div className="flex items-center gap-4 flex-wrap mb-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
          Podobne wygrane ({a.n})
        </p>
        <span className="text-sm">
          mediana <b className="text-emerald-600">{fmtPln(a.median_price)}</b>
        </span>
        <span className="text-xs text-[var(--color-text-muted)]">
          widełki {fmtPln(a.p25_price)} – {fmtPln(a.p75_price)}
        </span>
        <span className="text-xs text-[var(--color-text-muted)]">
          śr. ofert: <b>{a.avg_offers ?? "—"}</b>
        </span>
        {a.median_offer_spread_pct != null && (
          <span className="text-xs text-[var(--color-text-muted)]">
            rozbieżność ofert: <b>{a.median_offer_spread_pct}%</b>
          </span>
        )}
      </div>
      <div className="space-y-1">
        {d.similar.map((s) => (
          <div
            key={s.id}
            className="flex items-start justify-between gap-3 text-xs rounded-lg border border-[var(--color-border)] px-2.5 py-1.5"
          >
            <div className="min-w-0">
              <p className="leading-snug">
                <Link to={`/ezamowienia/p/${s.id}`} className="hover:underline">
                  {s.title}
                </Link>
              </p>
              <p className="text-[10px] text-[var(--color-text-muted)] mt-0.5 truncate">
                {s.organization_name} ({s.province}) · {fmtDate(s.resolved_at)} · wygrał:{" "}
                {s.winner_name || "—"}
              </p>
            </div>
            <div className="text-right shrink-0 tabular-nums">
              <p className="font-semibold text-emerald-600">{fmtPln(s.price_selected)}</p>
              <p className="text-[10px] text-[var(--color-text-muted)]">
                {s.offers_count != null ? `${s.offers_count} ofert` : "—"}
                {s.price_min && s.price_max && s.price_min !== s.price_max && (
                  <>
                    {" "}
                    · {fmtPln(s.price_min)}–{fmtPln(s.price_max)}
                  </>
                )}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Header/result facts of one proceeding — fetched from the detail endpoint. */
export function RowDetail({ id }: { id: string }) {
  const detail = useEzProceeding(id);
  if (detail.isLoading)
    return (
      <div className="py-4 flex justify-center">
        <Spinner />
      </div>
    );
  const d = detail.data;
  if (!d) return <p className="text-sm text-red-600 py-2">Nie udało się pobrać szczegółów.</p>;

  const cls = d.classification as { uzasadnienie?: string } | null;
  return (
    <div className="mt-3 pt-3 border-t border-[var(--color-border)] grid md:grid-cols-2 gap-x-8 gap-y-3">
      <div className="space-y-3">
        <Field label="Zamawiający">
          {d.organization_name}
          {d.organization_national_id && (
            <span className="text-[var(--color-text-muted)]"> · NIP {d.organization_national_id}</span>
          )}
        </Field>
        <Field label="CPV">{d.cpv_raw || "—"}</Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Publikacja">{fmtDate(d.first_publication_date)}</Field>
          <Field label="Termin ofert">{fmtDate(d.submitting_offers_date)}</Field>
        </div>
        {cls?.uzasadnienie && (
          <Field label="Klasyfikacja EZ-ARON">
            <span className="italic">„{cls.uzasadnienie}”</span>
          </Field>
        )}
        {d.stage_reason && !cls?.uzasadnienie && <Field label="Etap lejka">{d.stage_reason}</Field>}
      </div>

      <div className="space-y-3">
        {d.has_result ? (
          d.is_annulled ? (
            <Field label="Unieważnienie">
              {d.annulment_reason || "Postępowanie unieważnione (bez podanej przyczyny)."}
            </Field>
          ) : d.parts_count && d.parts_count > 1 ? (
            <Field label={`Wynik (${d.parts_count} części)`}>
              <span className="block space-y-0.5">
                {(d.result_parts ?? []).map((part, i) => (
                  <span key={i} className="block text-xs tabular-nums">
                    cz. {i + 1}: {fmtPln(part.price_selected as string | null)} —{" "}
                    {(part.winner_name as string) || "—"}
                  </span>
                ))}
              </span>
            </Field>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Cena wybrana">
                  <span className="font-semibold text-emerald-600">{fmtPln(d.price_selected)}</span>
                </Field>
                <Field label="Najniższa">{fmtPln(d.price_min)}</Field>
                <Field label="Najwyższa">{fmtPln(d.price_max)}</Field>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Ofert">{d.offers_count ?? "—"}</Field>
                <Field label="Odrzuconych">{d.offers_rejected_count ?? "—"}</Field>
                <Field label="Umowa z dnia">{fmtDate(d.contract_date)}</Field>
              </div>
              <Field label="Wykonawca">
                {d.winner_name || "—"}
                {d.winner_size && (
                  <span className="text-[var(--color-text-muted)]"> · {d.winner_size}</span>
                )}
              </Field>
              {d.realization_period && <Field label="Okres realizacji">{d.realization_period}</Field>}
            </>
          )
        ) : (
          <Field label="Wynik">
            Postępowanie w toku — wynik dopnie się automatycznie po rozstrzygnięciu.
          </Field>
        )}
        {d.order_value && (
          <Field label="Wartość zamówienia (szacunek zamawiającego)">{fmtPln(d.order_value)}</Field>
        )}
      </div>
    </div>
  );
}

/* One feed card. Title opens the dedicated proceeding page; the chevron gives a
   quick in-place peek. The reco reason preview answers "why?" at a glance. */
export function FeedCard({ row }: { row: ProceedingRow }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={clsx(
        "card border-l-4 px-4 py-3 transition-shadow hover:shadow-md",
        STAGE_META[row.stage].rail,
      )}
    >
      <div className="flex items-start gap-3">
        <button
          onClick={() => setOpen((o) => !o)}
          className="mt-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
          aria-label={open ? "Zwiń" : "Rozwiń"}
        >
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap mb-1">
            <StageChip stage={row.stage} />
            <RecoChip row={row} />
            <WorkModeChip mode={row.work_mode} />
            {row.tags.map((t) => (
              <TagChip key={t} tag={t} />
            ))}
          </div>
          <Link
            to={`/ezamowienia/p/${row.id}`}
            className="block font-medium text-sm leading-snug hover:text-rose-700 dark:hover:text-rose-400 transition-colors"
          >
            {row.title}
          </Link>
          <p className="text-xs text-[var(--color-text-muted)] mt-1 truncate">
            {row.organization_name} · {row.organization_city} ({row.organization_province})
            {row.cpv_main && <> · CPV {row.cpv_main}</>}
            {row.price_total && (
              <>
                {" "}
                · wygrana{" "}
                <span className="font-medium text-emerald-600">{fmtPln(row.price_total)}</span>
                {(row.parts_count ?? 1) > 1 && <> ({row.parts_count} cz.)</>}
                {row.offers_count != null && <> ({row.offers_count} ofert)</>}
              </>
            )}
          </p>
          {row.recommendation && row.recommendation_reason && (
            <p className="text-xs mt-1 line-clamp-2 text-[var(--color-text-secondary)]">
              <span className="font-semibold">Dlaczego:</span> {row.recommendation_reason}
            </p>
          )}
        </div>

        <div className="flex flex-col items-end gap-2 shrink-0">
          <div className="flex items-center gap-2">
            <DeadlinePill row={row} />
            {row.source_url && (
              <a
                href={row.source_url}
                target="_blank"
                rel="noreferrer"
                title="Otwórz na platformie zamawiającego"
                className="text-[var(--color-text-muted)] hover:text-rose-600"
              >
                <ExternalLink size={14} />
              </a>
            )}
          </div>
          <DecisionControl row={row} />
        </div>
      </div>
      {open && (
        <div className="pl-7">
          <RowDetail id={row.id} />
          <RecommendationBlock row={row} />
          <SimilarSection id={row.id} />
          <DocumentsSection id={row.id} />
        </div>
      )}
    </div>
  );
}
